import { useCallback } from "react";
import useAuth from ".";

export default function useAuthApi() {
    const auth = useAuth();

    const executeApiRequest = useCallback(
        async (options) => {
            const {
                field,
                value,
                apiEndpoint,
                method = "PATCH",
                updateLocalState,
                preprocessData,
                handleSuccess,
                handleError,
            } = options;

            if (!field) {
                throw new Error("Missing required field: 'field' in options");
            }
            if (!value) {
                throw new Error("Missing required field: 'value' in options");
            }
            if (!apiEndpoint) {
                throw new Error(
                    "Missing required field: 'apiEndpoint' in options"
                );
            }

            if (!auth.accessToken || !auth.deviceId) {
                throw new Error("Missing authentication token or device ID");
            }

            try {
                // Update local state if provided
                if (updateLocalState) {
                    updateLocalState(field, value);
                }

                // Prepare data for API submission
                let body;
                if (preprocessData) {
                    body = preprocessData(field, value);
                } else {
                    const formData = new FormData();
                    formData.append(field, value);
                    body = formData;
                }

                const response = await fetch(apiEndpoint, {
                    method: method,
                    headers: {
                        ...(method !== "GET" && body instanceof FormData
                            ? {}
                            : { "Content-Type": "application/json" }),
                        Authorization: `Bearer ${auth.accessToken}`,
                        "X-CC-Device-Id": auth.deviceId,
                    },
                    ...(method !== "GET"
                        ? {
                              body:
                                  body instanceof FormData
                                      ? body
                                      : JSON.stringify(body),
                          }
                        : {}),
                });

                if (!response.ok) {
                    throw new Error(
                        `API request failed with status ${response.status}`
                    );
                }

                const responseData = await response.json();

                if (!responseData.success) {
                    throw new Error(responseData.message);
                }

                // Handle success
                if (typeof handleSuccess === "function") {
                    handleSuccess(responseData);
                }

                return responseData;
            } catch (error) {
                console.error("Error in API operation:", error);

                // Handle error
                if (typeof handleError === "function") {
                    handleError(error);
                }

                throw error;
            }
        },
        [auth]
    );

    return executeApiRequest;
}
